import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { Spinner } from "./ui";
import { Database, Sparkles, ArrowRight } from "lucide-react";

// First-run welcome for a new owner: choose between seeded demo data or an empty workspace.
// Non-dismissable on purpose — the user must pick one before entering the app.
export default function OnboardingModal() {
  const { t, user, chooseOnboarding, toast } = useApp();
  const [busy, setBusy] = useState(null); // "demo" | "fresh" | null

  if (!user?.needs_onboarding) return null;

  const pick = async (choice) => {
    if (busy) return;
    setBusy(choice);
    try {
      await chooseOnboarding(choice);
    } catch {
      toast(t("onb_error"), { variant: "error" });
      setBusy(null);
    }
  };

  const Option = ({ choice, icon: Icon, title, desc, testid, accent }) => (
    <button
      type="button"
      onClick={() => pick(choice)}
      disabled={!!busy}
      data-testid={testid}
      className="group relative text-left rounded-xl border p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
      style={{ borderColor: "var(--border)" }}
    >
      <div className="flex items-center justify-center w-11 h-11 rounded-lg mb-4"
        style={{ background: `${accent}1f`, color: accent }}>
        {busy === choice ? <Spinner className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
      </div>
      <div className="font-heading font-semibold text-base mb-1 flex items-center gap-1.5">
        {title}
        <ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
      </div>
      <p className="text-sm text-text-secondary leading-relaxed">{desc}</p>
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      data-testid="onboarding-modal"
    >
      <div className="w-full max-w-2xl rounded-2xl border bg-surface shadow-2xl animate-fade-up overflow-hidden">
        <div className="px-7 pt-7 pb-2 text-center">
          <h2 className="text-2xl font-heading font-semibold mb-1.5" data-testid="onboarding-title">
            {t("onb_title")}
          </h2>
          <p className="text-sm text-text-secondary max-w-md mx-auto">{t("onb_subtitle")}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 px-7 py-6">
          <Option
            choice="demo"
            icon={Database}
            accent="#2563EB"
            title={t("onb_demo_title")}
            desc={t("onb_demo_desc")}
            testid="onboarding-choose-demo"
          />
          <Option
            choice="fresh"
            icon={Sparkles}
            accent="#10B981"
            title={t("onb_fresh_title")}
            desc={t("onb_fresh_desc")}
            testid="onboarding-choose-fresh"
          />
        </div>
        <div className="px-7 pb-5 text-center">
          <p className="text-[11px] text-text-secondary">{t("onb_footnote")}</p>
        </div>
      </div>
    </div>
  );
}
